import { Hidden, Paper, Tooltip, Typography } from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import { DataGrid, GridOverlay } from "@mui/x-data-grid";
import clsx from "clsx";
import { format, isValid, parseISO } from "date-fns";
import { Fragment, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { capitalize } from "shared/utils";
import { AuthContext } from "../../../../context/context";
import { SearchBar, Spinner } from "../../../../shared/components";
import cotizador from "../../../../services/cotizador";
import useStyles from "./styles";

const TableSectionsFlotillas = ({ setMessage, setShowMessage }) => {
  const classes = useStyles();
  const history = useHistory();
  const { token, setTimer } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState(0);
  const cardsPerPage = 2;

  const columns = [
    {
      field: "solicitud",
      headerName: "No. Solicitud",
      flex: 1.2,
      headerClassName: "header",
      headerAlign: "center",
    },
    {
      field: "grupo",
      headerName: "Grupo",
      flex: 1,
      headerClassName: "header",
      headerAlign: "center",
    },
    {
      field: "subgrupo",
      headerName: "Subgrupo",
      flex: 1,
      headerClassName: "header",
      headerAlign: "center",
    },
    {
      field: "descripcionStatus",
      headerName: "Estatus",
      flex: 1.2,
      headerClassName: "header",
      headerAlign: "center",
      renderCell: ({ row }) => (
        <Tooltip title={capitalize(row.descripcionStatus) || ""}>
          <span className={classes.tableCellTrucate}>
            {capitalize(row.descripcionStatus) || "No disponible"}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "fechaEmision",
      headerName: "Fecha",
      flex: 1,
      headerClassName: "header",
      headerAlign: "center",
      renderCell: ({ row }) => (
        <span className={classes.tableCellTrucate}>
          {row.fechaEmision || "No disponible"}
        </span>
      ),
    },
  ];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await cotizador.listaSoflosub({ usuario: token.cdUsuario });
      if (res.data) {
        setRows(res.data);
      } else {
        console.error("Error al obtener cotizaciones de flotillas:", res.error);
        setMessage({
          text: "No fue posible obtener las cotizaciones de flotillas.",
          severity: "warning",
        });
        setShowMessage(true);
      }
      setLoading(false);
    };
    load();
    // eslint-disable-next-line
  }, []);

  const formatDate = (value) => {
    if (!value) return "";
    const date = parseISO(value);
    if (!isValid(date)) return "";
    return format(date, "dd/MM/yyyy");
  };

  const handleRowClick = (row) => {
    setTimer(0);
    if (row.status === "W") {
      const inicioParam = row.inicioVigencia
        ? `&inicioVigencia=${formatDate(row.inicioVigencia)}`
        : "";
      history.push(
        `/alta-flotillas?grupo=${row.grupo}&noSolicitud=${row.solicitud}${inicioParam}`
      );
    }
  };

  const handleSearch = (e) => {
    setTimer(0);
    setPagination(0);
    setSearch(e.target.value);
  };

  const handleChangePagination = (e, value) => {
    setTimer(0);
    setPagination(value - 1);
  };

  const filterData = (rows) => {
    return rows.filter(
      (item) =>
        item.solicitud
          ?.toString()
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        item.grupo?.toString().toLowerCase().includes(search.toLowerCase()) ||
        item.subgrupo
          ?.toString()
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        item.descripcionStatus?.toLowerCase().includes(search.toLowerCase()) ||
        item.fechaEmision
          ?.toString()
          .toLowerCase()
          .includes(search.toLowerCase())
    );
  };

  const numberOfPages = Math.ceil(filterData(rows).length / 2);

  return (
    <Fragment>
      {loading && <Spinner />}

      <div className={classes.headerComponent}>
        <Typography variant="h5" className={classes.title} align="left">
          <strong>Cotización de flotillas recientes</strong>
        </Typography>
        <SearchBar change={handleSearch} search={search} />
      </div>
      <Hidden smDown>
        <DataGrid
          columns={columns}
          rows={filterData(rows)}
          className={classes.root}
          onCellClick={(e) => handleRowClick(e.row)}
          disableColumnMenu
          disableSelectionOnClick
          rowsPerPageOptions={[5]}
          pageSize={5}
          pagination
          components={{
            NoRowsOverlay: () => (
              <GridOverlay>No se encontró información</GridOverlay>
            ),
          }}
          getRowId={(row) => row.solicitud}
          getRowClassName={(params) =>
            params.row.status === "C" ? classes.disabledRow : ""
          }
        />
      </Hidden>
      <Hidden mdUp>
        <div className={classes.rowsMobile}>
          {filterData(rows)
            .slice(
              pagination * cardsPerPage,
              pagination * cardsPerPage + cardsPerPage
            )
            .map((row, index) => (
              <Paper
                elevation={3}
                className={clsx(
                  classes.card,
                  row.status === "C" && classes.disabledCard
                )}
                key={index}
                onClick={() => handleRowClick(row)}
              >
                <div className={classes.cardItem}>
                  <Typography variant="body1">
                    <strong>{row.solicitud}</strong>
                  </Typography>
                  <Typography variant="caption">No. Solicitud</Typography>
                </div>
                <div className={classes.cardItem}>
                  <Typography variant="body1">
                    <strong>{row.descripcionStatus}</strong>
                  </Typography>
                  <Typography variant="caption">Estatus</Typography>
                </div>
                <div className={classes.cardItem}>
                  <Typography variant="body1">
                    <strong>{row.grupo}</strong>
                  </Typography>
                  <Typography variant="caption">Grupo</Typography>
                </div>
                <div className={classes.cardItem}>
                  <Typography variant="body1">
                    <strong>{row.subgrupo}</strong>
                  </Typography>
                  <Typography variant="caption">Subgrupo</Typography>
                </div>
                <div className={classes.cardItem}>
                  <Typography variant="body1">
                    <strong>{row.fechaEmision || "No disponible"}</strong>
                  </Typography>
                  <Typography variant="caption">Fecha</Typography>
                </div>
              </Paper>
            ))}
        </div>

        <div className={classes.pagination}>
          <Pagination
            shape="rounded"
            count={numberOfPages}
            onChange={handleChangePagination}
            id="pagination"
          />
        </div>
      </Hidden>
    </Fragment>
  );
};

export default TableSectionsFlotillas;
